interface AFrameGlobal {
  components?: Record<string, unknown>;
  registerComponent?: (...args: unknown[]) => unknown;
}

declare global {
  interface Window {
    AFRAME?: AFrameGlobal;
  }
}

export const AR_VENDOR_SCRIPTS = {
  aframe: "/vendor/aframe.min.js",
  mindarImageAframe: "/vendor/mindar-image-aframe.prod.js",
} as const;

export class ArEngineLoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ArEngineLoadError";
  }
}

export interface ArEngineLoaderOptions {
  document?: Document;
  window?: Window;
  timeoutMs?: number;
  aframeSrc?: string;
  mindarSrc?: string;
}

let engineLoadPromise: Promise<void> | undefined;
const scriptLoadPromises = new Map<string, Promise<void>>();

export function loadArEngine(options: ArEngineLoaderOptions = {}): Promise<void> {
  const win = options.window ?? window;
  const doc = options.document ?? document;
  const timeoutMs = options.timeoutMs ?? 15_000;
  const aframeSrc = options.aframeSrc ?? AR_VENDOR_SCRIPTS.aframe;
  const mindarSrc = options.mindarSrc ?? AR_VENDOR_SCRIPTS.mindarImageAframe;

  if (isMindArAframeReady(win)) {
    return Promise.resolve();
  }

  if (engineLoadPromise) {
    return engineLoadPromise;
  }

  engineLoadPromise = (async () => {
    await loadScriptOnce(aframeSrc, doc, timeoutMs);
    assertAFrameReady(win);

    await loadScriptOnce(mindarSrc, doc, timeoutMs);
    assertMindArAframeReady(win);
  })().catch((error: unknown) => {
    engineLoadPromise = undefined;
    throw error;
  });

  return engineLoadPromise;
}

export function isAFrameReady(win: Window = window): boolean {
  return Boolean(win.AFRAME?.components && typeof win.AFRAME.registerComponent === "function");
}

export function isMindArAframeReady(win: Window = window): boolean {
  return Boolean(
    isAFrameReady(win) &&
      win.AFRAME?.components?.["mindar-image"] &&
      win.AFRAME.components["mindar-image-target"],
  );
}

export function __resetArLoaderForTests(): void {
  engineLoadPromise = undefined;
  scriptLoadPromises.clear();
}

function assertAFrameReady(win: Window): void {
  if (!isAFrameReady(win)) {
    throw new ArEngineLoadError("A-Frame carregou, mas window.AFRAME não ficou disponível.");
  }
}

function assertMindArAframeReady(win: Window): void {
  if (!isMindArAframeReady(win)) {
    throw new ArEngineLoadError("MindAR carregou, mas os componentes A-Frame não foram registados.");
  }
}

function loadScriptOnce(src: string, doc: Document, timeoutMs: number): Promise<void> {
  const existingLoadedScript = doc.querySelector<HTMLScriptElement>(
    `script[data-ar-engine-src="${cssEscape(src)}"][data-loaded="true"]`,
  );

  if (existingLoadedScript) {
    return Promise.resolve();
  }

  const existingPromise = scriptLoadPromises.get(src);

  if (existingPromise) {
    return existingPromise;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = doc.createElement("script");
    const timeout = doc.defaultView?.setTimeout(() => {
      script.remove();
      scriptLoadPromises.delete(src);
      reject(new ArEngineLoadError(`Timeout ao carregar ${src}.`));
    }, timeoutMs);

    const clear = (): void => {
      if (timeout) {
        doc.defaultView?.clearTimeout(timeout);
      }
    };

    script.src = src;
    script.async = false;
    script.dataset.arEngineSrc = src;

    script.addEventListener(
      "load",
      () => {
        clear();
        script.dataset.loaded = "true";
        resolve();
      },
      { once: true },
    );

    script.addEventListener(
      "error",
      () => {
        clear();
        script.remove();
        scriptLoadPromises.delete(src);
        reject(new ArEngineLoadError(`Erro ao carregar ${src}.`));
      },
      { once: true },
    );

    doc.head.append(script);
  });

  scriptLoadPromises.set(src, promise);
  return promise;
}

function cssEscape(value: string): string {
  if (typeof globalThis.CSS !== "undefined" && typeof globalThis.CSS.escape === "function") {
    return globalThis.CSS.escape(value);
  }

  return value.replace(/["\\]/g, "\\$&");
}
